/** All user-facing Analytics strings — English only */
export const ANALYTICS_COPY = Object.freeze({
  moduleTitle: 'Analytics',
  homeSubtitle: 'Review the reports you pinned, or create a new one.',
  welcomeTitle: 'Welcome to Analytics',
  welcomeSubtitle: 'Build custom reports from your Records, WhatsApp, and Intervene data.',
  welcomeSourcesTitle: 'Or start from a data source',
  sourceRecordsTitle: 'Records',
  sourceRecordsDescription: 'Counts and breakdowns from your lead tables.',
  sourceWhatsappTitle: 'WhatsApp',
  sourceWhatsappDescription: 'Sent, delivered, read, and click performance.',
  sourceInterveneTitle: 'Intervene',
  sourceInterveneDescription: 'Agent replies, response times, and agent benchmarking.',
  createFirstReport: 'Create your first report',
  browseTemplates: 'Browse starter templates',
  myAnalytics: 'My Analytics',
  reports: 'Reports',
  savedReportsTitle: 'Saved reports',
  savedReportsEmpty: 'You have not saved a report yet.',
  openReport: 'Open',
  pinReport: 'Pin',
  reportPinned: 'Pinned',
  pinReportFailed: 'Could not pin this report.',
  arrangeReports: 'Arrange',
  doneArranging: 'Done',
  arrangingHint: 'Drag a card by its title to reorder it. Drag the corner handle to resize it.',
  resizeReport: 'Resize report',
  reorderReportsFailed: 'Could not save this layout.',
  createReport: 'Create report',
  createReportSubtitle: 'Choose a data source, then set up the chart.',
  editReport: 'Edit report',
  editReportSubtitle: 'Update this report and save your changes.',
  deleteReport: 'Delete report',
  editReportAria: 'Edit report',
  deleteReportAria: 'Delete report',
  deleteReportTitle: 'Delete this report?',
  deleteReportMessage: 'This removes the saved report from Analytics. Pinned copies are removed too. This cannot be undone.',
  deleteReportConfirm: 'Delete',
  deleteReportCancel: 'Cancel',
  deleteReportFailed: 'Could not delete this report.',
  deleteReportSuccess: 'Report deleted.',
  saveChanges: 'Save changes',
  backToReport: 'Back to report',
  saveReportFailed: 'Could not save this report. Check your permissions or try again.',
  saveReportSuccess: 'Report saved.',
  createReportPermissionHint: 'You need “Create custom reports” permission to save changes.',
  selectOrganisation: 'Select an organisation to view analytics',
  selectOrganisationHint: 'Choose an organisation above to load business functions and services.',
  stepDataSource: 'Choose a data source',
  stepDataSourceHint: 'Pick the kind of report you want. Setup and a live preview open below.',
  stepConfigure: 'Configure your report',
  stepConfigureSubtitle:
    'Choose the table, pick one field, then watch the preview update.',
  stepConfigureSubtitleWithSource: 'Building a report from {source}. The preview updates as you choose.',
  builderStep1Title: 'Data source',
  builderStep1Subtitle: 'Choose which business function, service, and record table to use.',
  builderStep2Title: 'Narrow your data',
  builderStep2Subtitle: 'Filter rows and set a date range before building the chart.',
  builderStep2DateLabel: 'Date range',
  builderStep3Title: 'Visualization',
  builderStep3Subtitle: 'Pick how you want to see the results.',
  builderStep4Title: 'What to show',
  builderStep4Subtitle: 'Group records and choose what to calculate.',
  builderStep5Title: 'Name your report',
  builderStep5Subtitle: 'Give this report a clear name before saving.',
  scopeSectionTitle: 'Business scope',
  stepScope: 'Choose where to pull data from',
  stepScopeSubtitle:
    'Pick a business function. Then choose one or more services and record forms, or leave them empty to include all.',
  stepDefine: 'Define your report',
  businessFunctionLabel: 'Business function',
  businessFunctionPlaceholder: 'Select a business function',
  businessServiceLabel: 'Business service',
  businessServicePlaceholder: 'All business services',
  businessServiceHint: 'Optional. Choose one or more services, or leave empty to include every service.',
  noBusinessServices: 'No business services found for this function.',
  recordFormLabel: 'Record form',
  recordFormPlaceholder: 'All record forms',
  recordFormHint: 'Optional. Choose one or more forms, or leave empty to include every form in the selected services.',
  recordFormsLoading: 'Loading record forms…',
  noRecordForms: 'No record forms found for the selected services.',
  partialFieldHint: '{field} is only on {tables}. Other selected tables are not included in this chart.',
  fieldsGroupedHint: 'Fields under “On every selected table” apply to every selected table. A field under one table name leaves the other tables out of this chart.',
  scopeLoading: 'Loading your business functions…',
  scopeEmptyTitle: 'No business functions available',
  scopeEmptyDescription:
    'Ask your admin to grant Business Functions access in Profile Management, then try again.',
  scopeSelectOrgTitle: 'Select an organisation first',
  scopeSelectOrgDescription: 'Choose an organisation above to load business functions and services.',
  chartTypeSectionTitle: 'How do you want to see it?',
  configureChartTitle: 'Define the report',
  numberConfigureHint: 'One total for the selected scope and dates. Switch to Bar or Table to break down by a field.',
  lineConfigureHint: 'A trend over time using the date range above.',
  barConfigureHint: 'Compare groups side by side. Choose a field to break down by.',
  pieConfigureHint: 'Share of each category. Choose a field to break down by.',
  tableConfigureHint: 'Detailed rows. Choose the columns to show.',
  whatsappVizLockedHint: 'One overview for this date range. Choose a template to narrow it.',
  whatsappLineHint: 'One point per day, for the same date range as Number. Totals under the chart should match that overview.',
  whatsappTableHint: 'All templates is one row per template. One template is one row per day.',
  calculateFromColumn: 'Calculate from a numeric column',
  useRecordCountInstead: 'Use record count instead',
  previewViewChip: 'View: {label}',
  reportRecipeTitle: 'What should this chart show?',
  reportRecipeSubtitle: 'Pick table columns — group by one, then choose what to calculate.',
  reportPreviewLabel: 'YOUR REPORT',
  groupByStepLabel: 'Break down by',
  groupByStepLabelPie: 'Break down by',
  groupByStepLabelTable: 'Break down by',
  groupByStepLabelTime: 'Trend over time',
  groupByStepDescription: 'Each bar, slice, or row is one value in this field. You only pick the field once.',
  groupByStepDescriptionTime: 'Each point is one day.',
  tableColumnsLabel: 'Columns',
  tableColumnsPlaceholder: 'Choose columns to show',
  tableColumnsHint: 'These columns are shown for rows that match the date range and conditions.',
  groupByFieldLabel: 'Field',
  groupByFieldPlaceholder: 'Select a field…',
  calculateStepLabel: 'Show',
  calculateStepDescription: 'Count records, or sum and average a numeric field.',
  valueInclusionTitle: 'Only these values',
  valueInclusionOptional: 'Optional',
  valueInclusionHint: 'Leave empty to include every value.',
  valueInclusionBlank: 'Blank',
  valueInclusionLoading: 'Loading values…',
  valueInclusionEmpty: 'No saved values for this field yet.',
  valueInclusionSearch: 'Search values',
  alsoIncludeLabel: 'Also only include rows where…',
  alsoIncludeHint: 'Use this when the condition is a different field, such as City is Pune.',
  includeRowsLabel: 'Only include rows where',
  includeRowsHint: 'Optional. Add a condition when you want a subset of this table.',
  recordsMeasureLabel: 'Number of records',
  recordsMeasureHelper: 'Counts rows in each group — no numeric column needed.',
  recordsMeasureHelperLine: 'Counts records created on each day.',
  recordsMeasureHelperNumber: 'Counts matching rows in this date range.',
  calculateStepDescriptionNumber: 'This is the total number of matching records.',
  calculateStepDescriptionAdvanced: 'Sum, average, minimum, or maximum of a numeric column.',
  measureFieldLabel: 'Numeric column',
  measureFieldPlaceholder: 'Select an integer or boolean column…',
  xAxisSectionTitle: 'Horizontal axis',
  xAxisSectionSubtitle: 'Category field shown along the bottom',
  yAxisSectionTitle: 'Vertical axis',
  yAxisSectionSubtitle: 'How values are calculated and displayed',
  aggregationLabel: 'Calculation',
  aggregationCountHelper: 'Counts how many records fall in each category.',
  aggregationFieldHelper: 'Uses values from {field} in each record.',
  selectMeasureFieldHelper: 'Pick a numeric column from your table to aggregate.',
  noNumericFields: 'No integer or boolean columns found in this table. Use Count or add a supported field to your form.',
  chartSummaryPrefix: 'Showing',
  filtersSectionTitle: 'Only include rows where',
  filtersSectionSubtitle: 'Optional. Add a condition on a field other than the one you break down by.',
  filtersEmptyHint: 'No extra conditions. The chart includes every matching row.',
  addFilterLabel: 'Add condition',
  removeFilterLabel: 'Remove filter',
  filterFieldLabel: 'Field',
  filterFieldPlaceholder: 'Select a field…',
  filterOperatorLabel: 'Is',
  filterValueLabel: 'Value',
  filterValuePlaceholder: 'Enter or select a value…',
  filterChipLabel: 'Filter',
  measureLabel: 'Measure',
  defaultMeasureLabel: 'Record count',
  countMeasureHelper: 'Y-axis uses the number of records in the selected table.',
  numberMeasureHelper: 'Total records in the selected table for the date range.',
  xAxisCategoriesLabel: 'X-axis (categories)',
  xAxisSliceLabel: 'X-axis (slices)',
  xAxisGroupLabel: 'X-axis (group rows by)',
  xAxisTimeLabel: 'X-axis (time)',
  yAxisValuesLabel: 'Y-axis (values)',
  yAxisValuesHelper: 'What to measure on the vertical axis — usually record count.',
  groupByLabel: 'Group by (X-axis)',
  sliceByLabel: 'Slice by',
  rowsGroupedByLabel: 'Group rows by',
  trendByLabel: 'Trend by {field}',
  lineTrendHelper: 'Each point is the number of records created on that day.',
  tableFieldsFrom: 'Columns from the {table} table',
  tableFieldsGeneric: 'Columns from your selected record table',
  selectTableFirst: 'Select a business service and record form above to load table columns.',
  noTableColumns: 'No table columns available. Open a record table and use Create report, or select a form above.',
  pieTooManyCategories: 'This field has many values — a bar chart may be easier to read than a pie chart.',
  tableContextTitle: 'Report from this table',
  tableContextSubtitle: 'Data scope is taken from the table you opened.',
  previewNeedsDimension: 'Choose a field from this form to split the chart.',
  numberViewHint: 'This is the total for the selected scope and dates. Switch visualization to Bar or Table to break down by a field.',
  whatsappTemplateLabel: 'Template',
  whatsappTemplateAll: 'All templates',
  cardFiltersTitle: 'Filter this report',
  cardFiltersHint: 'Choose a template, user, or customer for this report only.',
  cardFiltersApply: 'Apply filters',
  cardFiltersClear: 'Clear filters',
  cardFiltersLoadFailed: 'Could not load filter options. Try again.',
  cardFiltersNoTemplates: 'No templates available for this organisation.',
  whatsappTemplateHint: 'All templates shows the organisation total. Choose one template to see only that template.',
  whatsappShowDescription: 'Choose which template metric to show.',
  whatsappGroupByDescription: 'Each bar, slice, or row is one template or one bot number.',
  whatsappGroupByHint: 'Template and bot number are the fields for this report.',
  whatsappTrendDescription: 'Each point is this metric for that day.',
  whatsappPreviewNeedsDimension: 'Choose a template or bot number to split the chart.',
  interveneNumberHint:
    'Summary for your organisation or the user you select below. Response times use paired replies only.',
  interveneAudienceTitle: 'Focus on one person',
  interveneAudienceDescription:
    'Optional: pick a team user to see response times and message counts for the date range.',
  interveneStartersTitle: 'Quick report starters',
  interveneStartersSubtitle: 'Start with a recommended configuration for Intervene:',
  intervenePresetAgentBenchmarkBar: 'Agent benchmarking (Chart)',
  intervenePresetAgentBenchmarkTable: 'Agent benchmarking (Table)',
  intervenePresetAgentMaster: 'Agent response master',
  intervenePresetTeamOverview: 'Team response overview',
  intervenePresetResponseTrend: 'Response time trend',
  interveneMasterAllTimeHint: 'All time can be slow for large teams. Last 30 days is recommended.',
  interveneAgentFilterLabel: 'User',
  interveneAgentFilterPlaceholder: 'All users',
  interveneAgentFilterHint: 'Team members with Intervene module access. Profile name is shown under each user.',
  interveneUserCountHint: '{count} users available',
  interveneUserListEmpty: 'No Intervene users found for this organisation.',
  interveneCustomerFilterLabel: 'Customer phone',
  interveneCustomerFilterPlaceholder: 'All customers',
  interveneCustomerFilterHint: 'WhatsApp number as stored in chat (digits only).',
  interveneShowDescription: 'Choose what to measure — message counts or average / median response time.',
  interveneGroupByDescription:
    'Each bar or table row is one agent, customer phone, status, direction, or bot number.',
  interveneGroupByHint:
    'Tip: Bar or Table + Agent + Avg agent response benchmarks agents by response time side by side.',
  interveneTrendDescription: 'Each point is inbound and outbound (human) message volume for that day.',
  intervenePreviewNeedsDimension: 'Choose a field under Break down by to split the chart.',
  interveneAdvancedFiltersTitle: 'More filters',
  interveneAdvancedFiltersHint: 'Narrow by bot number, message status, or direction.',
  interveneBenchmarkSessionsWithActivity: 'Sessions with activity',
  interveneBenchmarkSessionsStarted: 'Sessions started',
  interveneBenchmarkSessionsSubtitle:
    '{active} sessions with messages · {total} sessions started',
  interveneBenchmarkNoLineDetails:
    'No paired reply in this session (customer message → agent reply).',
  interveneBenchmarkEmptyTitle: 'No agent response pairs yet',
  interveneBenchmarkEmptySteps:
    'Start an Intervene session, receive a customer message, then send a human agent reply.',
  interveneBenchmarkNoReplyChip: 'No agent reply',
  createReportFromTable: 'Create report',
  questionTypeLabel: 'What do you want to see?',
  singleNumber: 'Single total',
  trend: 'Change over time',
  compare: 'Compare by group',
  table: 'Detailed table',
  compareByLabel: 'Compare by',
  metricLabel: 'Metric',
  datePresetToday: 'Today',
  datePresetYesterday: 'Yesterday',
  datePresetLast7: 'Last 7 days',
  datePresetLast30: 'Last 30 days',
  datePresetLast90: 'Last 90 days',
  datePresetThisMonth: 'This month',
  datePresetAllTime: 'All time',
  datePresetToday: 'Today',
  datePresetThisWeek: 'This week',
  datePresetHintAllTime: 'Uses every row in the selected table.',
  datePresetHintAllTimeWhatsapp: 'Includes the full history of template activity.',
  datePresetHintAllTimeLine: 'One point per day, across the full history.',
  datePresetHintToday: 'Only rows created since midnight.',
  datePresetHintThisWeek: 'Only rows created since Monday.',
  datePresetCustom: 'Custom',
  datePresetReportDefault: 'Report default',
  datePresetHint: 'Saved as this report’s usual range. Analytics can show another range.',
  datePresetCustomLocked: 'Saved custom range: {range}. Choose a preset to replace it.',
  datePresetCustomHint: 'Applies to every pinned report in this view.',
  dateRangeApplyFailed: 'Could not apply this date range.',
  dateRangeLabel: 'Start date',
  dateRangeEndLabel: 'End date',
  dateRangeHint: 'Leave empty to include every date.',
  dateRangeInvalid: 'End date must be on or after the start date.',
  dateRangeClearLabel: 'Clear date range',
  reportNameLabel: 'Report name',
  saveReport: 'Save report',
  pinToDashboard: 'Pin to My Analytics',
  skipPin: 'Not now',
  pinDialogTitle: 'Pin this report?',
  pinDialogBody: 'Show this report on your Analytics home for quick access.',
  previewSectionTitle: 'Live preview',
  previewSectionSubtitle: 'See how your report looks before saving.',
  previewLoading: 'Loading preview…',
  previewEmpty: 'Adjust your choices to see a preview.',
  previewNoData: 'No records matched your filters. Try a different field or date range.',
  previewNeedsScope: 'Select a business function above to load the preview.',
  formFieldsLoading: 'Loading form fields for this business service…',
  formFieldsSelectServiceHint:
    'Select a business service to unlock custom form field breakdowns.',
  formFieldsSelectRecordFormHint:
    'Select a record form to unlock form-specific field breakdowns.',
  chartToggleNumber: 'Number',
  chartToggleChart: 'Chart',
  chartToggleTable: 'Table',
  noPinnedReports: 'No pinned reports yet. Save a report and pin it to your home.',
  backToHome: 'Back to Analytics',
  exportCsv: 'Export Excel',
  exportFailed: 'Could not download this report.',
  blankBreakdownNote: 'Unknown means these records have no value in {field}.',
  blankBreakdownField: 'the chosen field',
  tableTotal: 'Total',
  builderSteps: Object.freeze([
    'Data source',
    'Configure & preview',
  ]),
});
